/**
 * Custom hook for managing reCAPTCHA badge visibility and state with network awareness
 */
import { useEffect, useCallback, useState } from 'react';
import { waitForRecaptcha } from '@/lib/utils/recaptcha';
import { log } from '@/lib/utils/logger';
import { CSS_SELECTORS, RECAPTCHA_CONSTANTS } from '@/lib/constants/appConstants';

interface UseRecaptchaBadgeProps {
  isSlowNetwork?: boolean;
  enabled?: boolean;
}

export const useRecaptchaBadge = (props: UseRecaptchaBadgeProps = {}) => {
  const { isSlowNetwork = false, enabled = true } = props;
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);

  // Helper function to get badge element
  const getBadgeElement = useCallback((): HTMLElement | null => {
    return document.querySelector(CSS_SELECTORS.RECAPTCHA.BADGE) as HTMLElement;
  }, []);

  // Show badge with loading state
  const showBadgeWithLoading = useCallback(() => {
    const badge = getBadgeElement();
    if (badge) {
      badge.classList.add("visible", "loading");
      log.debug('reCAPTCHA badge shown with loading state');
    }
  }, [getBadgeElement]);

  // Show badge without loading state (ready state)
  const showBadgeReady = useCallback(() => {
    const badge = getBadgeElement();
    if (badge) {
      badge.classList.add("visible");
      badge.classList.remove("loading");
      log.debug('reCAPTCHA badge shown in ready state');
    }
  }, [getBadgeElement]);

  // Hide badge completely
  const hideBadge = useCallback(() => {
    const badge = getBadgeElement();
    if (badge) {
      badge.classList.remove("visible", "loading");
      log.debug('reCAPTCHA badge hidden');
    }
  }, [getBadgeElement]);

  // Remove only loading state while keeping badge visible
  const removeLoadingState = useCallback(() => {
    const badge = getBadgeElement();
    if (badge) {
      badge.classList.remove("loading");
      log.debug('reCAPTCHA badge loading state removed');
    }
  }, [getBadgeElement]);

  // Add loading state to visible badge
  const addLoadingState = useCallback(() => {
    const badge = getBadgeElement();
    if (badge) {
      badge.classList.add("loading");
      log.debug('reCAPTCHA badge loading state added');
    }
  }, [getBadgeElement]);

  // Show reCAPTCHA badge when component mounts with enhanced network awareness
  useEffect(() => {
    // Skip all initialization if reCAPTCHA is disabled
    if (!enabled) {
      log.debug('reCAPTCHA badge initialization skipped (disabled)');
      return;
    }

    let badgeTimeout: NodeJS.Timeout;
    let healthCheckInterval: NodeJS.Timeout;
    let retryCount = 0;
    let isDestroyed = false;
    
    const maxRetries = isSlowNetwork 
      ? RECAPTCHA_CONSTANTS.RETRIES.MAX_BADGE_RETRIES_SLOW
      : RECAPTCHA_CONSTANTS.RETRIES.MAX_BADGE_RETRIES;
    
    const retryInterval = isSlowNetwork
      ? RECAPTCHA_CONSTANTS.RETRIES.RETRY_INTERVAL_SLOW
      : RECAPTCHA_CONSTANTS.RETRIES.RETRY_INTERVAL;
    
    // Health check function to verify reCAPTCHA remains ready
    const performHealthCheck = () => {
      if (isDestroyed) return;
      
      const badge = getBadgeElement();
      if (badge && !window.grecaptcha?.enterprise?.execute) {
        log.warn('reCAPTCHA became unavailable, attempting to recover...');
        setIsRecaptchaReady(false);
        showBadgeWithLoading();
        // Restart initialization
        setTimeout(() => {
          if (!isDestroyed) {
            initializeBadge();
          }
        }, 1000);
      }
    };
    
    const initializeBadge = () => {
      if (isDestroyed) return;
      
      const badge = getBadgeElement();
      if (badge) {
        showBadgeWithLoading();
        
        // Wait for reCAPTCHA to be fully ready with enhanced network awareness
        waitForRecaptcha(undefined, isSlowNetwork).then((isReady) => {
          if (isDestroyed) return;
          
          if (isReady) {
            setIsRecaptchaReady(true);
            showBadgeReady();
            log.debug(`reCAPTCHA badge ready (slow network: ${isSlowNetwork})`);
            
            // Start health monitoring for slow networks
            if (isSlowNetwork && !healthCheckInterval) {
              healthCheckInterval = setInterval(performHealthCheck, 30000); // Check every 30s
            }
          } else {
            const timeout = isSlowNetwork ? 30000 : 10000;
            log.warn(`reCAPTCHA failed to load properly (slow network: ${isSlowNetwork}, timeout: ${timeout}ms)`);
            setIsRecaptchaReady(false);
            
            // On slow networks, show badge anyway but with warning state
            if (isSlowNetwork) {
              showBadgeReady(); // Show anyway but user gets feedback that button is disabled
              log.warn('reCAPTCHA not ready on slow network - submit button will be disabled');
            } else {
              showBadgeReady(); // Show badge even if there's an error on fast networks
            }
          }
        }).catch((error) => {
          if (isDestroyed) return;
          
          log.error(`Error waiting for reCAPTCHA (slow network: ${isSlowNetwork}):`, error);
          setIsRecaptchaReady(false);
          showBadgeReady(); // Show badge even if there's an error
        });
      } else {
        retryCount++;
        if (retryCount < maxRetries && !isDestroyed) {
          // Exponential backoff for badge element detection on slow networks
          const backoffMultiplier = isSlowNetwork ? 1.1 : 1.2;
          const nextRetryInterval = Math.min(
            Math.floor(retryInterval * Math.pow(backoffMultiplier, retryCount - 1)), 
            RECAPTCHA_CONSTANTS.RETRIES.MAX_RETRY_INTERVAL
          );
          
          badgeTimeout = setTimeout(initializeBadge, nextRetryInterval);
          log.debug(`reCAPTCHA badge element not found, retrying... (${retryCount}/${maxRetries}, slow network: ${isSlowNetwork}, next retry in: ${nextRetryInterval}ms)`);
        } else if (!isDestroyed) {
          log.warn(`reCAPTCHA badge element not found after maximum retries (${maxRetries}, slow network: ${isSlowNetwork})`);
          // On slow networks, we might still want to show some UI feedback
          if (isSlowNetwork) {
            log.warn('Badge element missing on slow network - this may indicate connection issues');
          }
        }
      }
    };
    
    // Start initialization with initial delay for slow networks to allow script loading
    const initialDelay = isSlowNetwork ? 2000 : 500;
    badgeTimeout = setTimeout(initializeBadge, initialDelay);
    
    return () => {
      isDestroyed = true;
      if (badgeTimeout) {
        clearTimeout(badgeTimeout);
      }
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }
    };
  }, [enabled, getBadgeElement, showBadgeWithLoading, showBadgeReady, isSlowNetwork]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      log.debug('Component unmounting - cleaning up reCAPTCHA badge...');
      hideBadge();
    };
  }, [hideBadge]);

  return { 
    showBadgeWithLoading,
    showBadgeReady,
    hideBadge,
    addLoadingState,
    removeLoadingState,
    getBadgeElement,
    isRecaptchaReady
  };
};
